import { EGlobalSearchItemType } from "../types/global-search";
import { EGlobalSearchGate } from "../types/global-search-index";
import {
  EGlobalSearchTab,
  EGlobalSearchTabBrowse,
  type IGlobalSearchTabDefinition,
} from "../types/global-search-tabs";

// NOTE: NSAI-6853 — the palette's category tabs, in display order. Every item
// type except ACTION and NOTIFICATION belongs to a tab besides ALL;
// SETTINGS_TAB / SETTINGS_OPTION (by settings area) and API_KEY (by scope)
// are split between SETTINGS and ORGANIZATION_SETTINGS (see
// isGlobalSearchItemInTab). Gates hide a tab whose whole area the user cannot
// open, using the same gate values as the static index.
export const GLOBAL_SEARCH_TABS: readonly IGlobalSearchTabDefinition[] = [
  {
    tab: EGlobalSearchTab.ALL,
    labelKey: "tabs.all",
    types: [],
    browse: EGlobalSearchTabBrowse.CHATS,
    hasServerResults: true,
    gates: [],
  },
  {
    tab: EGlobalSearchTab.CHATS,
    labelKey: "tabs.chats",
    types: [
      EGlobalSearchItemType.CHAT,
      EGlobalSearchItemType.CHAT_MESSAGE,
      EGlobalSearchItemType.CHAT_FOLDER,
    ],
    browse: EGlobalSearchTabBrowse.CHATS,
    hasServerResults: true,
    gates: [],
  },
  {
    tab: EGlobalSearchTab.PROJECTS,
    labelKey: "tabs.projects",
    types: [EGlobalSearchItemType.PROJECT],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: false,
    gates: [],
  },
  {
    tab: EGlobalSearchTab.ASSISTANTS,
    labelKey: "tabs.assistants",
    types: [EGlobalSearchItemType.ASSISTANT],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.ASSISTANT_READ],
  },
  {
    tab: EGlobalSearchTab.PROMPTS,
    labelKey: "tabs.prompts",
    types: [EGlobalSearchItemType.PROMPT],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.PROMPT_LIBRARY_READ],
  },
  {
    tab: EGlobalSearchTab.SKILLS,
    labelKey: "tabs.skills",
    types: [EGlobalSearchItemType.SKILL, EGlobalSearchItemType.CATALOG_SKILL],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.SKILLS_ENABLED],
  },
  {
    tab: EGlobalSearchTab.AGENTS,
    labelKey: "tabs.agents",
    types: [
      EGlobalSearchItemType.AUTOMATION,
      EGlobalSearchItemType.MARKETPLACE_AUTOMATION,
    ],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.AGENTS_ENABLED],
  },
  {
    tab: EGlobalSearchTab.GENERATED_FILES,
    labelKey: "tabs.generated_files",
    types: [EGlobalSearchItemType.GENERATED_FILE],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.API_AI_GENERATED_FILES],
  },
  {
    tab: EGlobalSearchTab.KNOWLEDGE,
    labelKey: "tabs.knowledge",
    types: [
      EGlobalSearchItemType.KNOWLEDGE_FILE,
      EGlobalSearchItemType.DOC_RECOGNITION_DOCUMENT,
    ],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [],
  },
  {
    tab: EGlobalSearchTab.MEETINGS,
    labelKey: "tabs.meetings",
    types: [
      EGlobalSearchItemType.MEETING,
      EGlobalSearchItemType.CALENDAR_EVENT,
    ],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.CALL_ASSISTANT_ENABLED],
  },
  {
    tab: EGlobalSearchTab.INTEGRATIONS,
    labelKey: "tabs.integrations",
    types: [
      EGlobalSearchItemType.INTEGRATION_PROVIDER,
      EGlobalSearchItemType.INTEGRATION_CONNECTION,
      EGlobalSearchItemType.CUSTOM_INTEGRATION,
      EGlobalSearchItemType.CUSTOM_TOOL,
    ],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.INTEGRATIONS_PAGE],
  },
  {
    tab: EGlobalSearchTab.MODELS,
    labelKey: "tabs.models",
    types: [EGlobalSearchItemType.MODEL],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.MODEL_READ],
  },
  {
    tab: EGlobalSearchTab.PAGES,
    labelKey: "tabs.pages",
    types: [EGlobalSearchItemType.PAGE],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: false,
    gates: [],
  },
  {
    tab: EGlobalSearchTab.SETTINGS,
    labelKey: "tabs.settings",
    types: [
      EGlobalSearchItemType.SETTINGS_TAB,
      EGlobalSearchItemType.SETTINGS_OPTION,
      EGlobalSearchItemType.MEMORY_FACT,
      EGlobalSearchItemType.CUSTOM_INSTRUCTIONS,
      // NOTE: Personal keys only — isGlobalSearchItemInTab splits API keys by
      // scope between this tab and ORGANIZATION_SETTINGS.
      EGlobalSearchItemType.API_KEY,
    ],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [],
  },
  {
    tab: EGlobalSearchTab.ORGANIZATION_SETTINGS,
    labelKey: "tabs.organization_settings",
    types: [
      EGlobalSearchItemType.SETTINGS_TAB,
      EGlobalSearchItemType.SETTINGS_OPTION,
      EGlobalSearchItemType.ORGANIZATION,
      EGlobalSearchItemType.MEMBER,
      EGlobalSearchItemType.INVITE,
      EGlobalSearchItemType.ROLE,
      EGlobalSearchItemType.API_KEY,
      EGlobalSearchItemType.PROBE,
    ],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: true,
    gates: [EGlobalSearchGate.IN_ORGANIZATION],
  },
  {
    tab: EGlobalSearchTab.HELP,
    labelKey: "tabs.help",
    types: [EGlobalSearchItemType.FAQ_ITEM],
    browse: EGlobalSearchTabBrowse.ITEMS,
    hasServerResults: false,
    gates: [],
  },
] as const;

// NOTE: The static-index types an ITEMS tab lists while the box is empty.
// Settings options are left out: they are only worth listing once matched.
export const GLOBAL_SEARCH_BROWSE_STATIC_TYPES: readonly EGlobalSearchItemType[] =
  [EGlobalSearchItemType.PAGE, EGlobalSearchItemType.SETTINGS_TAB] as const;
